import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { React, useState, useEffect } from "react";
import CustomAppBar from "../components/AppBar";
import { CustomTitle } from "../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import api from "../globals/axios";
import OrderCard from "../components/Biochar/OrderCard";
import OrderDetailsDialog from "../components/Biochar/OrderDetailsDialog";
import { useNavigate } from "react-router-dom";
import { processCashPayment } from "../api/biocharOrderPayment";
import { useAlert } from "../components/Alert";

const UserBiocharOrders = () => {
  const { t } = useTranslation();
  const [biocharOrders, setBiocharOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cashPaymentDialogOpen, setCashPaymentDialogOpen] = useState(false);
  const [orderForCashPayment, setOrderForCashPayment] = useState(null);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const fetchBiocharOrders = async () => {
    try {
      const response = await api.get(`/biochar/biochar-order/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Biochar orders:", error);
      throw error;
    }
  };

  useEffect(() => {
    const getBiocharOrders = async () => {
      try {
        setLoading(true);
        setError("");
        const biocharOrders = await fetchBiocharOrders();
        setBiocharOrders(biocharOrders.data || []);
      } catch (error) {
        console.error("Failed to fetch biochar orders:", error);
        setError("Failed to fetch orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    getBiocharOrders();
  }, []);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleMakePayment = (order) => {
    if (order.payment_mode === "CASH") {
      setOrderForCashPayment(order);
      setCashPaymentDialogOpen(true);
    } else {
      navigate(`/biochar-order-details/${order.id}`);
    }
  };

  const handleCashPaymentConfirm = async () => {
    if (!orderForCashPayment) return;

    try {
      setLoading(true);
      const result = await processCashPayment(orderForCashPayment.id);
      if (result.status === "SUCCESS") {
        showAlert("success", t("Cash payment recorded successfully!"));
        // Refresh the orders list
        const biocharOrders = await fetchBiocharOrders();
        setBiocharOrders(biocharOrders.data || []);
      } else {
        showAlert(
          "error",
          result.message || t("Failed to process cash payment")
        );
      }
    } catch (err) {
      console.error("Cash payment processing failed:", err);
      showAlert(
        "error",
        t("Failed to process cash payment. Please try again.")
      );
    } finally {
      setLoading(false);
      setCashPaymentDialogOpen(false);
      setOrderForCashPayment(null);
    }
  };

  const handleCashPaymentCancel = () => {
    setCashPaymentDialogOpen(false);
    setOrderForCashPayment(null);
  };

  const handleDownloadInvoice = (orderId) => {
    const orderToDownload = biocharOrders.find((order) => order.id === orderId);

    if (orderToDownload?.invoice_link) {
      window.open(orderToDownload.invoice_link, "_blank");
    } else {
      console.error("Invoice link not found for order ID:", orderId);
    }
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <CustomTitle>{t("Biochar Orders")}</CustomTitle>

        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : biocharOrders.length > 0 ? (
          biocharOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onViewDetails={handleViewDetails}
              customButtons={
                order.order_status === "PAID"
                  ? [
                      {
                        label: "Download Invoice",
                        onClick: () => handleDownloadInvoice(order.id),
                        color: "secondary",
                        disabled: !order.invoice_link,
                      },
                    ]
                  : [
                      {
                        label: "Make Payment",
                        onClick: () => handleMakePayment(order),
                        color: "secondary",
                      },
                    ]
              }
            />
          ))
        ) : (
          <Box sx={{ textAlign: "center", marginTop: 3 }}>
            <Typography variant="body1">{t("No orders found")}</Typography>
          </Box>
        )}

        <OrderDetailsDialog
          open={detailsOpen}
          order={selectedOrder}
          onClose={() => setDetailsOpen(false)}
          customActions={
            selectedOrder && selectedOrder.order_status !== "PAID"
              ? [
                  {
                    label: "Make Payment",
                    onClick: () => handleMakePayment(selectedOrder),
                    color: "secondary",
                  },
                ]
              : null
          }
        />

        {/* Cash Payment Confirmation Dialog */}
        <Dialog
          open={cashPaymentDialogOpen}
          onClose={handleCashPaymentCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("Confirm Cash Payment")}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {t(
                "Are you sure you want to mark this cash payment as completed?"
              )}
            </Typography>
            {orderForCashPayment && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{t("Order ID")}:</strong> #{orderForCashPayment.id}
                </Typography>
                <Typography variant="body2">
                  <strong>{t("Amount")}:</strong> ₹
                  {orderForCashPayment.total_amount_payable}
                </Typography>
                <Typography variant="body2">
                  <strong>{t("Payment Mode")}:</strong> {t("Cash Payment")}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCashPaymentCancel} color="primary">
              {t("Cancel")}
            </Button>
            <Button
              onClick={handleCashPaymentConfirm}
              color="primary"
              variant="contained"
              disabled={loading}
            >
              {loading ? t("Processing...") : t("Confirm Payment")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default UserBiocharOrders;
